import { useAnytimeProductList } from 'Modules/AnytimeTicket/Queries/useAnytimeProductList';
import {
	getPendingAnytimeTicketPurchase,
	clearPendingAnytimeTicketPurchase,
} from 'Modules/AnytimeTicket/Utils/AnytimeTicketPurchasePendingFlag';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import { observer } from 'mobx-react';
import Icon from '../Icon/Icon';
import { FormattedMessage } from 'react-intl';
import * as React from 'react';

const AnytimeTicketModal = React.lazy(() => import('Modules/AnytimeTicket/Components/AnytimeTicketModal'));

interface Props {
	anytimeColour?: string;
}

function AnytimeTicketButton({ anytimeColour }: Props) {
	const [showAnytimeTicketModal, setShowAnytimeTicketModal] = React.useState(false);
	const { anytimeTicketingEnabled } = appConfigStore;
	const { data: anytimeProducts, isLoading } = useAnytimeProductList();

	React.useEffect(() => {
		if (getPendingAnytimeTicketPurchase()) {
			clearPendingAnytimeTicketPurchase();
			setShowAnytimeTicketModal(true);
		}
	}, []);

	if (!anytimeTicketingEnabled || isLoading) {
		return null;
	}

	const hasAnytimeTickets = anytimeProducts?.some(p => !p.isDisabled && p.type === 'TICKET') ?? false;
	const hasAnytimePosItems = anytimeProducts?.some(p => !p.isDisabled && p.type === 'POS') ?? false;

	const showAnytimeLink = hasAnytimeTickets || (store.can('SellPosProducts') && hasAnytimePosItems);

	if (!showAnytimeLink) {
		return null;
	}

	return (
		<>
			<div className="begin-booking__divider" />
			<div
				className="begin-booking__anytime-link"
				data-test-id="home-anytime-ticket-button"
				// Left unset when no colour is configured, rather than defaulted here: the stylesheet's
				// own translucent fallback then applies. React omits an undefined value, so the custom
				// property is genuinely absent.
				style={{ '--anytime-colour': anytimeColour } as React.CSSProperties}
				onClick={() => setShowAnytimeTicketModal(true)}
				role="button"
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter') setShowAnytimeTicketModal(true);
				}}
			>
				<div className="begin-booking__anytime-left">
					<Icon
						name={appConfigStore.anytimeTicketingIconName}
						classname="begin-booking__anytime-icon"
						useMaterialIcons
					/>
					<div className="begin-booking__anytime-text">
						<span className="begin-booking__anytime-label">
							<FormattedMessage
								id="homepage__anytime-ticket-card__title"
								defaultMessage="Purchase anytime ticket"
							/>
						</span>
						<span className="begin-booking__anytime-subtitle">
							<FormattedMessage
								id="homepage__anytime-ticket-card__subtitle"
								defaultMessage="Travel whenever you want"
							/>
						</span>
					</div>
				</div>
				<Icon name="arrow_forward" classname="begin-booking__anytime-arrow" useMaterialIcons />
			</div>
			{showAnytimeTicketModal && (
				<React.Suspense fallback={null}>
					<AnytimeTicketModal
						isOpen={showAnytimeTicketModal}
						onClose={() => setShowAnytimeTicketModal(false)}
					/>
				</React.Suspense>
			)}
		</>
	);
}

export default observer(AnytimeTicketButton);
